import React from 'react';
import { useTheme } from '@/contexts/ThemeContext';

export interface TabItem {
  id: string;
  label: string;
  icon?: string;
}

interface TabNavigationProps {
  tabs: TabItem[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  className?: string;
}

/**
 * Reusable tab navigation component with consistent styling across the application.
 * Follows the same pattern as CoachDashboard tabs with proper theme support.
 */
const TabNavigation: React.FC<TabNavigationProps> = ({
  tabs,
  activeTab,
  onTabChange,
  className = ''
}) => {
  const { isDark } = useTheme();

  return (
    <div className={`mb-8 hover:scale-101 transition-all duration-300 ${className}`}>
      <div className={`${isDark ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-lg rounded-2xl p-2 px-4 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-lg overflow-visible`}>
        {/* Make tab bar fill the entire container */}
        <div className="flex w-full space-x-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              style={{ outline: 'none', transformOrigin: 'center' }}
              onClick={() => onTabChange(tab.id)}
              className={`cgm-text-primary flex items-center justify-center space-x-2 px-4 py-3 rounded-xl font-medium transition-all duration-200 flex-1 min-w-0 active:shadow-xl active:scale-95 
                ${activeTab === tab.id
                  ? 'cgm-text-tertiary bg-gradient-to-r from-purple-600 to-blue-600 shadow-lg transform scale-105 z-10'
                  : `${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`
                }`}
            >
              {tab.icon && <span className="flex-shrink-0">{tab.icon}</span>}
              <span className="hidden sm:block truncate">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TabNavigation; 